import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase.config";
import {
  getDoc,
  doc,
  getDocs,
  collection,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { FiUser } from "react-icons/fi";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../contexts/AuthContext";
import { FiLoader } from "react-icons/fi";
import {
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiImage,
  FiMapPin,
  FiPhone,
  FiStar,
  FiAward,
  FiArrowLeft,
  FiCheck,
  FiX,
  FiUpload,
  FiPackage,
  FiClock,
  FiGlobe,
} from "react-icons/fi";
import { MdStorefront, MdVerified } from "react-icons/md";

const ShowroomProfile = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // State variables
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showroomId, setShowroomId] = useState(null);
  const [updateSellerData, setUpdateSellerData] = useState({});
  const [updateListingData, setUpdateListingData] = useState({
    manufacturer: "",
    modelName: "",
    color: "",
    price: "",
    mileage: "",
    ranking: "",
    city: "",
    transmission: "",
    phoneNumber: "",
    modelYear: "",
    carImages: [],
  });
  const [sellerUpdateFormVisible, setSellerUpdateFormVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [currentListingId, setCurrentListingId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [progress, setProgress] = useState(0);

  // Fetch seller profile and listings for the authenticated user
  useEffect(() => {
    const fetchShowroomData = async () => {
      if (!currentUser) {
        setError("Please login to view your showroom");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Query the Feature-Seller collection for the current user's showroom
        const sellersRef = collection(db, "Feature-Seller");
        const q = query(sellersRef, where("userId", "==", currentUser.uid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setError("You haven't registered a showroom yet.");
          setLoading(false);
          return;
        }

        // Get the first showroom (should only be one per user)
        const sellerDoc = querySnapshot.docs[0];
        const sellerData = sellerDoc.data();
        const sellerId = sellerDoc.id;

        setSeller(sellerData);
        setUpdateSellerData(sellerData);
        setShowroomId(sellerId);

        // Fetch listings for this showroom
        const listingsRef = collection(
          db,
          "Feature-Seller",
          sellerId,
          "listings",
        );
        const listingsSnapshot = await getDocs(listingsRef);
        const listingsList = listingsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        listingsList.sort((a, b) => (a.ranking || 0) - (b.ranking || 0));
        setListings(listingsList);
      } catch (error) {
        setError(`Error fetching showroom data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchShowroomData();
  }, [currentUser]);

  // Handle image removal
  const handleRemoveImage = async (index) => {
    if (
      !updateListingData.carImages ||
      updateListingData.carImages.length === 0
    )
      return;

    const imageUrl = updateListingData.carImages[index];
    if (!imageUrl) return;

    const imageRef = ref(storage, imageUrl);

    try {
      await deleteObject(imageRef);
      const updatedImages = updateListingData.carImages.filter(
        (_, i) => i !== index,
      );
      const listingDocRef = doc(
        db,
        "Feature-Seller",
        showroomId,
        "listings",
        currentListingId,
      );

      await updateDoc(listingDocRef, {
        carImages: updatedImages,
      });

      setUpdateListingData((prevData) => ({
        ...prevData,
        carImages: updatedImages,
      }));

      toast.success("Image deleted successfully!");
    } catch (error) {
      toast.error(`Error deleting image: ${error.message}`);
    }
  };

  // Update seller profile
  const handleSellerProfileUpdate = async () => {
    try {
      const sellerDocRef = doc(db, "Feature-Seller", showroomId);
      await updateDoc(sellerDocRef, updateSellerData);
      setSeller(updateSellerData);
      setSellerUpdateFormVisible(false);
      toast.success("Showroom profile updated successfully!");
    } catch (error) {
      toast.error(`Error updating showroom profile: ${error.message}`);
    }
  };

  // Thumbnail upload
  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const storageRef = ref(
      storage,
      `sellers/${showroomId}/thumbnail/${file.name}`,
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
        setProgress(progress);
      },
      (error) => toast.error(`Error uploading thumbnail: ${error.message}`),
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const sellerDocRef = doc(db, "Feature-Seller", showroomId);
          await updateDoc(sellerDocRef, { thumbnail: downloadURL });
          setUpdateSellerData((prevData) => ({
            ...prevData,
            thumbnail: downloadURL,
          }));
          setSeller((prevData) => ({ ...prevData, thumbnail: downloadURL }));
          toast.success("Thumbnail uploaded successfully!");
        } catch (error) {
          toast.error(`Error saving thumbnail URL: ${error.message}`);
        }
      },
    );
  };

  // Handle Image Click to set as Primary
  const handleImageClick = async (imageURL) => {
    const updatedImages = [
      imageURL,
      ...updateListingData.carImages.filter((img) => img !== imageURL),
    ];

    try {
      const listingDocRef = doc(
        db,
        "Feature-Seller",
        showroomId,
        "listings",
        currentListingId,
      );
      await updateDoc(listingDocRef, {
        carImages: updatedImages,
      });

      setUpdateListingData((prevData) => ({
        ...prevData,
        carImages: updatedImages,
        primaryImage: imageURL,
      }));

      toast.success("Primary image updated!");
    } catch (error) {
      toast.error(`Error updating primary image: ${error.message}`);
    }
  };

  // Image upload for listings
  const handleImageSelection = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedImages.length > 6) {
      toast.error("You can select up to 6 images only.");
      return;
    }

    const uploadPromises = files.map((file) => {
      const storageRef = ref(
        storage,
        `listings/${showroomId}/${currentListingId}/${file.name}`,
      );
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          null,
          (error) => reject(error),
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          },
        );
      });
    });

    try {
      const urls = await Promise.all(uploadPromises);
      setUpdateListingData((prevData) => ({
        ...prevData,
        carImages: [...prevData.carImages, ...urls],
      }));
      toast.success("Images uploaded successfully!");
    } catch (error) {
      toast.error(`Error uploading images: ${error.message}`);
    }
  };

  // Delete listing
  const handleDeleteListing = async (listingId) => {
    if (!window.confirm("Are you sure you want to delete this listing?"))
      return;

    try {
      const listingDocRef = doc(
        db,
        "Feature-Seller",
        showroomId,
        "listings",
        listingId,
      );
      await deleteDoc(listingDocRef);
      setListings((prev) => prev.filter((listing) => listing.id !== listingId));
      toast.success("Listing deleted successfully!");
    } catch (error) {
      toast.error(`Failed to delete listing: ${error.message}`);
    }
  };

  // Update listing
  const handleUpdateListing = async () => {
    try {
      const listingDocRef = doc(
        db,
        "Feature-Seller",
        showroomId,
        "listings",
        currentListingId,
      );

      const updatedData = {
        ...updateListingData,
        primaryImage:
          updateListingData.primaryImage ||
          updateListingData.carImages?.[0] ||
          "",
        phoneNumber: updateListingData.phoneNumber,
      };

      await updateDoc(listingDocRef, updatedData);
      toast.success("Listing updated successfully!");
      setUpdateModalVisible(false);

      // Refresh listings
      const listingsRef = collection(
        db,
        "Feature-Seller",
        showroomId,
        "listings",
      );
      const listingsSnapshot = await getDocs(listingsRef);
      const listingsList = listingsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setListings(listingsList);
    } catch (error) {
      toast.error(`Error updating listing: ${error.message}`);
    }
  };

  // Redirect to add listing form
  const handleRedirectToAddListing = () => {
    navigate(`/add-car-to-showroom/${showroomId}`);
  };

  // Redirect to register showroom
  const handleRedirectToRegister = () => {
    navigate("/register-showroom");
  };

  // Loader and error handling
  if (loading) return <FiLoader className="animate-spin text-2xl" />;

  // If no showroom found, show registration prompt
  if (!seller && error === "You haven't registered a showroom yet.") {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center min-h-[70vh] px-4">
          <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-2xl w-full text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <MdStorefront className="text-5xl text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              No Showroom Found
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              You haven't registered a showroom yet. Register your showroom to
              start listing vehicles and reaching customers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleRedirectToRegister}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
              >
                Register Showroom
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  if (error)
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center min-h-[70vh] px-4">
          <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiX className="text-4xl text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
            <p className="text-gray-600 mb-8">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200"
            >
              Go Home
            </button>
          </div>
        </div>
        <ToastContainer />
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200 mb-6"
        >
          <FiArrowLeft className="text-lg" />
          <span>Back to Home</span>
        </button>

        {/* Seller Profile Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="relative h-32 bg-gradient-to-r from-blue-500 to-purple-600"></div>
          <div className="relative px-6 pb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-end -mt-16">
              <img
                src={seller.thumbnail || "https://via.placeholder.com/150"}
                className="w-32 h-32 rounded-full border-4 border-white shadow-xl object-cover"
                alt="Showroom Logo"
              />
              <div className="sm:ml-6 mt-4 sm:mt-0 text-center sm:text-left flex-1">
                <h1 className="text-3xl font-bold text-gray-900">
                  {seller.sellerName}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className="inline-flex items-center gap-1 text-gray-600">
                    <FiUser className="text-sm" />
                    {seller.businessName ? seller.businessName : "Individual Seller"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-gray-600">
                    <MdVerified className="text-blue-500" />
                    {seller.verified
                      ? "Verified Showroom"
                      : "Pending Verification"}
                  </span>
                  <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-sm">
                    <FiAward className="text-sm" />
                    Rank: {seller.ranking || "N/A"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSellerUpdateFormVisible(true)}
                className="mt-4 sm:mt-0 px-6 py-2 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-all duration-200 flex items-center gap-2"
              >
                <FiEdit2 className="text-sm" />
                Edit Profile
              </button>
            </div>

            {/* Seller Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <FiPhone className="text-blue-500 text-xl" />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-medium">{seller.phoneNumber}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <FiMapPin className="text-blue-500 text-xl" />
                <div>
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="font-medium">{seller.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <FiPackage className="text-blue-500 text-xl" />
                <div>
                  <p className="text-xs text-gray-500">Specialization</p>
                  <p className="font-medium">
                    {seller.specialization || "General Automotive"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <FiClock className="text-blue-500 text-xl" />
                <div>
                  <p className="text-xs text-gray-500">Experience</p>
                  <p className="font-medium">
                    {seller.yearsOfExperience || "0"} years
                  </p>
                </div>
              </div>
            </div>

            {seller.description && (
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="text-gray-700">{seller.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Add Listing Button */}
        <div className="flex justify-between items-center mt-8">
          <h2 className="text-2xl font-bold text-gray-900">My Listings</h2>
          <button
            onClick={handleRedirectToAddListing}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg transition duration-300 flex items-center gap-2"
          >
            <FiPlus className="text-xl" />
            Add New Listing
          </button>
        </div>

        {/* Listings Grid */}
        {listings.length === 0 ? (
          <div className="mt-8 bg-white rounded-2xl p-12 text-center border-2 border-dashed border-gray-300">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiPackage className="text-3xl text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Listings Yet
            </h3>
            <p className="text-gray-500 mb-6">
              Start by adding your first vehicle listing
            </p>
            <button
              onClick={handleRedirectToAddListing}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-all duration-200"
            >
              Add Your First Listing
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((listing) => (
              <div
                key={listing.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition duration-300"
              >
                <div className="relative h-48 bg-gray-200">
                  <img
                    src={listing.carImages?.[0] || "default-car-image.jpg"}
                    alt={listing.modelName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    #{listing.ranking || "N/A"}
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-lg font-semibold text-blue-600">
                    {listing.manufacturer} {listing.modelName}
                  </h3>
                  <p className="text-gray-700 font-medium">
                    Rs {listing.price?.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                    <FiMapPin className="text-sm" />
                    {listing.city || "Location"}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <FiPhone className="text-sm" />
                    {listing.phoneNumber}
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        setCurrentListingId(listing.id);
                        setUpdateListingData(listing);
                        setUpdateModalVisible(true);
                      }}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-xl hover:bg-blue-700 transition duration-200 flex items-center justify-center gap-1 text-sm"
                    >
                      <FiEdit2 className="text-sm" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteListing(listing.id)}
                      className="flex-1 bg-red-500 text-white px-3 py-2 rounded-xl hover:bg-red-600 transition duration-200 flex items-center justify-center gap-1 text-sm"
                    >
                      <FiTrash2 className="text-sm" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Update Seller Modal */}
      {sellerUpdateFormVisible && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Update Showroom Profile
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Showroom Name
                </label>
                <input
                  type="text"
                  value={
                    updateSellerData.businessName || updateSellerData.sellerName
                  }
                  onChange={(e) =>
                    setUpdateSellerData((prevData) => ({
                      ...prevData,
                      businessName: e.target.value,
                      sellerName: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Owner Name
                </label>
                <input
                  type="text"
                  value={updateSellerData.sellerName}
                  onChange={(e) =>
                    setUpdateSellerData((prevData) => ({
                      ...prevData,
                      sellerName: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={updateSellerData.phoneNumber}
                  onChange={(e) =>
                    setUpdateSellerData((prevData) => ({
                      ...prevData,
                      phoneNumber: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={updateSellerData.address}
                  onChange={(e) =>
                    setUpdateSellerData((prevData) => ({
                      ...prevData,
                      address: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specialization
                </label>
                <select
                  value={
                    updateSellerData.specialization || "General Automotive"
                  }
                  onChange={(e) =>
                    setUpdateSellerData((prevData) => ({
                      ...prevData,
                      specialization: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                >
                  <option value="Luxury Vehicles">Luxury Vehicles</option>
                  <option value="Sports Cars">Sports Cars</option>
                  <option value="SUVs & 4x4s">SUVs & 4x4s</option>
                  <option value="Electric Vehicles">Electric Vehicles</option>
                  <option value="Vintage Cars">Vintage Cars</option>
                  <option value="Commercial Vehicles">
                    Commercial Vehicles
                  </option>
                  <option value="Motorcycles">Motorcycles</option>
                  <option value="Auto Parts">Auto Parts</option>
                  <option value="Car Services">Car Services</option>
                  <option value="General Automotive">General Automotive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Years in Business
                </label>
                <input
                  type="number"
                  value={updateSellerData.yearsOfExperience || "0"}
                  onChange={(e) =>
                    setUpdateSellerData((prevData) => ({
                      ...prevData,
                      yearsOfExperience: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={updateSellerData.description || ""}
                  onChange={(e) =>
                    setUpdateSellerData((prevData) => ({
                      ...prevData,
                      description: e.target.value,
                    }))
                  }
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
                  placeholder="Tell customers about your showroom..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Update Logo
                </label>
                <input
                  type="file"
                  onChange={handleThumbnailUpload}
                  className="w-full p-2 border border-gray-300 rounded-xl"
                  accept="image/*"
                />
                {progress > 0 && progress < 100 && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {progress}% uploaded
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSellerProfileUpdate}
                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition duration-200 flex items-center justify-center gap-2"
              >
                <FiCheck className="text-lg" />
                Save Changes
              </button>
              <button
                onClick={() => setSellerUpdateFormVisible(false)}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-300 transition duration-200 flex items-center justify-center gap-2"
              >
                <FiX className="text-lg" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Listing Modal */}
      {updateModalVisible && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Update Listing
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer
                </label>
                <input
                  type="text"
                  value={updateListingData.manufacturer || ""}
                  onChange={(e) =>
                    setUpdateListingData((prevData) => ({
                      ...prevData,
                      manufacturer: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Name
                </label>
                <input
                  type="text"
                  value={updateListingData.modelName || ""}
                  onChange={(e) =>
                    setUpdateListingData((prevData) => ({
                      ...prevData,
                      modelName: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (Rs)
                </label>
                <input
                  type="number"
                  value={updateListingData.price || ""}
                  onChange={(e) =>
                    setUpdateListingData((prevData) => ({
                      ...prevData,
                      price: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={updateListingData.city || ""}
                  onChange={(e) =>
                    setUpdateListingData((prevData) => ({
                      ...prevData,
                      city: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={updateListingData.phoneNumber || ""}
                  onChange={(e) =>
                    setUpdateListingData((prevData) => ({
                      ...prevData,
                      phoneNumber: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ranking
                </label>
                <input
                  type="number"
                  value={updateListingData.ranking || ""}
                  onChange={(e) =>
                    setUpdateListingData((prevData) => ({
                      ...prevData,
                      ranking: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transmission
                </label>
                <div className="flex gap-4 p-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="Manual"
                      checked={updateListingData.transmission === "Manual"}
                      onChange={(e) =>
                        setUpdateListingData((prevData) => ({
                          ...prevData,
                          transmission: e.target.value,
                        }))
                      }
                      className="w-4 h-4 text-blue-600"
                    />
                    Manual
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="Automatic"
                      checked={updateListingData.transmission === "Automatic"}
                      onChange={(e) =>
                        setUpdateListingData((prevData) => ({
                          ...prevData,
                          transmission: e.target.value,
                        }))
                      }
                      className="w-4 h-4 text-blue-600"
                    />
                    Automatic
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add Images
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageSelection}
                  className="w-full p-2 border border-gray-300 rounded-xl"
                />
                <p className="text-xs text-gray-500 mt-1">Max 6 images</p>
              </div>
            </div>

            {/* Image Gallery */}
            {updateListingData.carImages &&
              updateListingData.carImages.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Images (Click to set as primary)
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {updateListingData.carImages.map((imageURL, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={imageURL}
                          alt={`Car ${index + 1}`}
                          className="w-24 h-24 object-cover rounded-xl cursor-pointer border-2 border-transparent hover:border-blue-500 transition"
                          onClick={() => handleImageClick(imageURL)}
                        />
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                        >
                          <FiX className="text-sm" />
                        </button>
                        {index === 0 && (
                          <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs text-center py-0.5 rounded-b-xl">
                            Primary
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdateListing}
                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition duration-200 flex items-center justify-center gap-2"
              >
                <FiCheck className="text-lg" />
                Save Changes
              </button>
              <button
                onClick={() => setUpdateModalVisible(false)}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-300 transition duration-200 flex items-center justify-center gap-2"
              >
                <FiX className="text-lg" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default ShowroomProfile;
